import { Check, AlertTriangle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IDEOLOGY_AXES, type IdeologyVector } from '@/constants/ideology'
import {
  distanceToSimilarity,
  calculateRawDistance,
  normalizeDistance,
  getAlignmentLabel,
} from '@/lib/ideology'

interface IdeologyComparisonProps {
  /** First entity's ideology vector */
  vectorA: IdeologyVector
  /** Second entity's ideology vector */
  vectorB: IdeologyVector
  /** Label for first entity (e.g., "Candidate", "Green Party") */
  labelA?: string
  /** Label for second entity (e.g., "Party", "Social Democrats") */
  labelB?: string
  /** Custom color for entity A marker (hex color) */
  colorA?: string
  /** Custom color for entity B marker (hex color) */
  colorB?: string
  /** Salience weights for distance calculation */
  weights?: IdeologyVector
  /** Show overall distance score (default: true) */
  showDistance?: boolean
  /** Show per-axis distance indicators (default: true) */
  showAxisDistances?: boolean
  /** Compact mode for smaller displays */
  compact?: boolean
  className?: string
}

/**
 * @deprecated Use vectorA/vectorB props instead. These are kept for backward compatibility.
 */
interface LegacyIdeologyComparisonProps {
  candidateIdeology: IdeologyVector
  partyIdeology: IdeologyVector
  className?: string
}

interface AxisAlignment {
  difference: number
  label: string
  colorClass: string
  bgClass: string
  icon: 'check' | 'warning' | 'x'
}

function getAxisAlignment(valueA: number, valueB: number): AxisAlignment {
  const diff = Math.abs(valueA - valueB)

  if (diff <= 10) {
    return {
      difference: diff,
      label: 'Close',
      colorClass: 'text-green-600 dark:text-green-400',
      bgClass: 'bg-green-500',
      icon: 'check',
    }
  }
  if (diff <= 25) {
    return {
      difference: diff,
      label: 'Minor',
      colorClass: 'text-yellow-600 dark:text-yellow-400',
      bgClass: 'bg-yellow-500',
      icon: 'check',
    }
  }
  if (diff <= 50) {
    return {
      difference: diff,
      label: 'Divergent',
      colorClass: 'text-orange-600 dark:text-orange-400',
      bgClass: 'bg-orange-500',
      icon: 'warning',
    }
  }
  return {
    difference: diff,
    label: 'Opposed',
    colorClass: 'text-red-600 dark:text-red-400',
    bgClass: 'bg-red-500',
    icon: 'x',
  }
}

function AlignmentIcon({
  icon,
  className,
}: {
  icon: AxisAlignment['icon']
  className?: string
}) {
  switch (icon) {
    case 'check':
      return <Check className={cn('h-3 w-3', className)} />
    case 'warning':
      return <AlertTriangle className={cn('h-3 w-3', className)} />
    case 'x':
      return <X className={cn('h-3 w-3', className)} />
  }
}

function valueToPercent(value: number): number {
  return ((value + 100) / 200) * 100
}

/**
 * Get alignment color class based on similarity percentage.
 */
function getAlignmentTextColor(alignmentPercent: number): string {
  if (alignmentPercent >= 80) return 'text-green-600 dark:text-green-400'
  if (alignmentPercent >= 60) return 'text-yellow-600 dark:text-yellow-400'
  if (alignmentPercent >= 40) return 'text-orange-600 dark:text-orange-400'
  return 'text-red-600 dark:text-red-400'
}

/**
 * Generic ideology comparison component for comparing any two entities.
 *
 * Features:
 * - Side-by-side comparison on all 7 ideology axes
 * - Dual markers showing each entity's position per axis
 * - Overall alignment/distance score
 * - Per-axis alignment indicators
 * - Customizable labels and colors
 *
 * Works for comparing:
 * - Party vs Party
 * - Candidate vs Party
 * - Cohort vs Party
 * - Any two IdeologyVector entities
 */
export function IdeologyComparison(
  props: IdeologyComparisonProps | LegacyIdeologyComparisonProps
) {
  // Handle both new and legacy prop formats
  const isLegacy = 'candidateIdeology' in props
  const vectorA = isLegacy ? props.candidateIdeology : props.vectorA
  const vectorB = isLegacy ? props.partyIdeology : props.vectorB
  const labelA = isLegacy ? 'Candidate' : (props.labelA ?? 'Entity A')
  const labelB = isLegacy ? 'Party' : (props.labelB ?? 'Entity B')
  const colorA = isLegacy ? undefined : props.colorA
  const colorB = isLegacy ? undefined : props.colorB
  const showDistance = isLegacy ? true : (props.showDistance ?? true)
  const showAxisDistances = isLegacy ? true : (props.showAxisDistances ?? true)
  const compact = isLegacy ? false : (props.compact ?? false)

  const distance = calculateRawDistance(vectorA, vectorB)
  const alignmentPercent = distanceToSimilarity(distance)
  const normalizedDist = normalizeDistance(distance)
  const alignmentLabel = getAlignmentLabel(normalizedDist)

  // Determine marker styles
  const markerAStyle = colorA ? { backgroundColor: colorA } : undefined
  const markerAClass = colorA ? '' : 'bg-blue-500'
  const markerBStyle = colorB ? { borderColor: colorB } : undefined
  const markerBClass = colorB ? '' : 'border-primary'

  return (
    <div className={cn('space-y-4', compact && 'space-y-2', props.className)}>
      {/* Header with legend and overall alignment */}
      <div className={cn(
        'flex items-center justify-between',
        compact && 'text-xs'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div
              className={cn('h-2 w-2 rounded-full', markerAClass)}
              style={markerAStyle}
            />
            <span className="text-xs text-muted-foreground">{labelA}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className={cn(
                'h-2 w-2 rounded-full border-2 bg-transparent',
                markerBClass
              )}
              style={markerBStyle}
            />
            <span className="text-xs text-muted-foreground">{labelB}</span>
          </div>
        </div>
        {showDistance && (
          <div className="text-right">
            <span
              className={cn(
                compact ? 'text-sm font-semibold' : 'text-lg font-bold',
                getAlignmentTextColor(alignmentPercent)
              )}
            >
              {alignmentPercent}% Aligned
            </span>
            {!compact && (
              <div className={cn(
                'text-xs',
                getAlignmentTextColor(alignmentPercent)
              )}>
                {alignmentLabel}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-axis comparison */}
      <div className={cn('space-y-3', compact && 'space-y-2')}>
        {IDEOLOGY_AXES.map((axis, index) => {
          const valueA = vectorA[index]
          const valueB = vectorB[index]
          const alignment = getAxisAlignment(valueA, valueB)
          const posA = valueToPercent(valueA)
          const posB = valueToPercent(valueB)

          return (
            <div key={axis.key} className="space-y-1">
              <div className={cn(
                'flex items-center justify-between text-xs',
                compact && 'text-[10px]'
              )}>
                <span className="font-medium">{axis.label}</span>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground tabular-nums">
                    {valueA > 0 ? '+' : ''}{valueA}
                    <span className="mx-1">vs</span>
                    {valueB > 0 ? '+' : ''}{valueB}
                  </span>
                  {showAxisDistances && (
                    <>
                      <AlignmentIcon icon={alignment.icon} className={alignment.colorClass} />
                      <span className={cn('font-medium', alignment.colorClass)}>
                        {alignment.label}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Dual-position bar */}
              <div className={cn(
                'relative h-2 w-full rounded-full bg-muted',
                compact && 'h-1.5'
              )}>
                {/* Center line marker */}
                <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />

                {/* Entity A marker (filled circle) */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-background z-10',
                    compact ? 'h-2.5 w-2.5' : 'h-3 w-3',
                    markerAClass
                  )}
                  style={{
                    left: `${posA}%`,
                    ...markerAStyle,
                  }}
                  title={`${labelA}: ${valueA}`}
                />

                {/* Entity B marker (hollow circle) */}
                <div
                  className={cn(
                    'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-background z-10',
                    compact ? 'h-2.5 w-2.5' : 'h-3 w-3',
                    markerBClass
                  )}
                  style={{
                    left: `${posB}%`,
                    ...markerBStyle,
                  }}
                  title={`${labelB}: ${valueB}`}
                />
              </div>

              {/* Axis labels */}
              {!compact && (
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{axis.leftLabel}</span>
                  <span>{axis.rightLabel}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Compact inline comparison showing just the overall alignment.
 * Useful for tables, cards, or lists where space is limited.
 */
interface CompactComparisonProps {
  vectorA: IdeologyVector
  vectorB: IdeologyVector
  labelA?: string
  labelB?: string
  colorA?: string
  colorB?: string
  className?: string
}

export function CompactIdeologyComparison({
  vectorA,
  vectorB,
  labelA,
  labelB,
  colorA,
  colorB,
  className,
}: CompactComparisonProps) {
  const distance = calculateRawDistance(vectorA, vectorB)
  const alignmentPercent = distanceToSimilarity(distance)
  const normalizedDist = normalizeDistance(distance)
  const alignmentLabel = getAlignmentLabel(normalizedDist)

  const markerAStyle = colorA ? { backgroundColor: colorA } : undefined
  const markerAClass = colorA ? '' : 'bg-blue-500'
  const markerBStyle = colorB ? { borderColor: colorB } : undefined
  const markerBClass = colorB ? '' : 'border-primary'

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Legend dots */}
      <div className="flex items-center gap-1">
        <div
          className={cn('h-2 w-2 rounded-full', markerAClass)}
          style={markerAStyle}
          title={labelA}
        />
        <div
          className={cn('h-2 w-2 rounded-full border-2 bg-transparent', markerBClass)}
          style={markerBStyle}
          title={labelB}
        />
      </div>

      {/* Alignment text */}
      <span
        className={cn(
          'text-sm font-medium',
          getAlignmentTextColor(alignmentPercent)
        )}
      >
        {alignmentPercent}%
      </span>
      <span className="text-xs text-muted-foreground">{alignmentLabel}</span>
    </div>
  )
}
